/**
 * brandAssets — the brand graphics a manager can drop into an IMAGE layer via the
 * design editor's "Grafik" picker.
 *
 * Two sources, ONE merged list (mergeBrandAssets):
 *  - the curated SEED below: static assets under `public/static/social/`, resolved
 *    server-side through DesignRenderer.resolveAssetSrc and loaded by URL on the
 *    client (one src, both ends);
 *  - the manager's own UPLOADS from the self-serve library, each addressed by its
 *    `/api/social/assets/:id/file` URL that both ends resolve the same way (the
 *    server renderer reads the DB Buffer, the client loads the URL).
 *
 * Every asset in either source is a src the renderer can provably resolve — the
 * seed ships on disk, an upload is round-trip-gated by brandAssetSanitizer — so a
 * design can never reference a graphic the renderer cannot draw.
 *
 * ESM, zero deps (mirrors bindOptions.js / presets.js).
 *
 * @module DesignEditor/shared/brandAssets
 */

// { src, label } — the on-disk asset path + a German label for the picker.
const BRAND_ASSETS = [
    { src: '/static/social/horsedeal-logomark-white.svg', label: 'HorseDeal Logo (weiss)' },
    { src: '/static/social/pin-white.svg', label: 'Standort-Pin (weiss)' },
    { src: '/static/social/link-gradient.svg', label: 'Link-Symbol' },
    { src: '/static/social/heart-purple.svg', label: 'Herz (violett)' },
];

/**
 * Merge the curated seed with the manager's uploaded assets into ONE picker list.
 * Curated first (the on-brand starters lead), then uploads, deduped by src so an
 * asset never appears twice. Pure — safe to call on every render.
 *
 * @param {Array<{src:string,label:string}>} curated
 * @param {Array<{src:string,label:string}>} uploaded
 * @returns {Array<{src:string,label:string}>}
 */
const mergeBrandAssets = (curated = [], uploaded = []) => {
    const seen = new Set();
    const out = [];
    for (const asset of [...curated, ...uploaded]) {
        if (!asset || !asset.src || seen.has(asset.src)) continue;
        seen.add(asset.src);
        out.push(asset);
    }
    return out;
};

/** The label for a brand-asset src, or '' if it is not a curated asset. */
const brandAssetLabel = src => {
    const found = BRAND_ASSETS.find(a => a.src === src);
    return found ? found.label : '';
};

export { BRAND_ASSETS, mergeBrandAssets, brandAssetLabel };
